import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminSupportAPI } from '../../../services/api';

export interface Message {
  id: number;
  body: string;
  is_admin_reply: boolean;
  sender_name: string;
  created_at: string;
}

export interface TicketDetailData {
  id: number;
  ticket_id: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  messages: Message[];
  message_count: number;
  company_name: string;
  created_at: string;
  updated_at: string;
}

export const statusColors: Record<string, { bg: string; text: string }> = {
  open: { bg: '#dbeafe', text: '#1e40af' },
  in_progress: { bg: '#fef3c7', text: '#92400e' },
  waiting: { bg: '#fce7f3', text: '#9d174d' },
  resolved: { bg: '#d1fae5', text: '#065f46' },
  closed: { bg: '#f3f4f6', text: '#6b7280' },
};

export const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting on Customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const categoryLabels: Record<string, string> = {
  general: 'General Question',
  billing: 'Billing & Payments',
  technical: 'Technical Issue',
  account: 'Account & Settings',
  feature: 'Feature Request',
  bug: 'Bug Report',
  other: 'Other',
};

export const useAdminTicketDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadTicket = useCallback(async () => {
    try {
      const res = await adminSupportAPI.getTicket(Number(id));
      setTicket(res.data);
    } catch {
      setError('Ticket not found.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTicket();
  }, [loadTicket]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      await adminSupportAPI.reply(Number(id), replyBody.trim());
      setReplyBody('');
      await loadTicket();
    } catch {
      setError('Failed to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    try {
      await adminSupportAPI.closeTicket(Number(id));
      await loadTicket();
    } catch {
      setError('Failed to close ticket.');
    }
  };

  const handleReopen = async () => {
    try {
      await adminSupportAPI.reopenTicket(Number(id));
      await loadTicket();
    } catch {
      setError('Failed to reopen ticket.');
    }
  };

  const handleResolve = async () => {
    try {
      await adminSupportAPI.resolveTicket(Number(id));
      await loadTicket();
    } catch {
      setError('Failed to resolve ticket.');
    }
  };

  const formatDate = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit',
    });
  };

  const isClosed = ticket?.status === 'closed' || ticket?.status === 'resolved';

  const navigateToSupport = () => navigate('/manage/support');

  return {
    ticket,
    loading,
    replyBody,
    setReplyBody,
    sending,
    error,
    messagesEndRef,
    handleReply,
    handleClose,
    handleReopen,
    handleResolve,
    formatDate,
    isClosed,
    navigateToSupport,
  };
};
